/**
 * WaveformPlayer — `.ws-wrap` from the prototype
 * (cms-redesign-mockup.html ~4745–4848 CSS, ~8375–8398 markup,
 * ~11279–11347 JS). A wavesurfer.js v7-backed visualization of the
 * session audio with click-to-seek + colored marker overlays for
 * the three timestamp families (content start / rating window /
 * tapping points).
 *
 *   <WaveformPlayer
 *     url={formData.session_main_url_preview || formData.session_main_url}
 *     uploading={formData.session_main_url_uploading}
 *     onUpload={handleMainMediaUpload}
 *     onLoadedMetadata={handleAudioLoadedData}
 *     onRemove={() => …}
 *     contentStart={formData.session_content_start_timestamp}
 *     ratingStart={formData.session_rating_start}
 *     ratingEnd={formData.session_rating_end}
 *     showRating={!!formData.session_allow_rating}
 *     tappingPoints={formData.session_bg_timestamps}
 *     tappingPointsCatalog={tapping_points}
 *   />
 *
 * Implementation notes:
 *   • Wavesurfer is instantiated once per `url` — when the URL
 *     changes (new upload), the previous instance is destroyed
 *     and a fresh one is created. `url=''` shows the upload tile.
 *   • Markers live in a sibling `.ws-markers` div, positioned by
 *     `left: (t/duration*100)%`. Reposition reactively any time a
 *     marker prop changes; clicking one seeks the player to that
 *     time.
 *   • The play/pause control swaps SVGs based on `isPlaying()`.
 *   • `onLoadedMetadata` is invoked once duration is known, with a
 *     shape compatible with v1's `e.target.duration` consumer.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { cn } from '../cn';

/* ─── Helpers ──────────────────────────────────────────────────── */

function fmt(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return (
    String(Math.floor(s / 60)).padStart(2, '0') +
    ':' +
    String(s % 60).padStart(2, '0')
  );
}

// Two-letter pip for a tapping point. Prefers the first two letters
// of words in the name ("Under Arm" → "UA"); falls back to first two
// chars of the single word.
function tpInitials(name) {
  if (!name) return '·';
  const words = String(name).trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return words[0].slice(0, 2).toUpperCase();
}

function deriveFilename(url) {
  if (!url) return '';
  if (url.startsWith('blob:') || url.startsWith('data:')) return '(pending upload)';
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').filter(Boolean).pop();
    return last ? decodeURIComponent(last) : url;
  } catch {
    const last = String(url).split('/').filter(Boolean).pop();
    return last || String(url);
  }
}

/* ─── Icons ────────────────────────────────────────────────────── */

const PLAY_ICON = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <polygon points="6 4 20 12 6 20 6 4" />
  </svg>
);

const PAUSE_ICON = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="5" width="4" height="14" rx="1" />
    <rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);

const UPLOAD_ICON = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

/* ─── Markers ──────────────────────────────────────────────────── */

function Marker({ type, time, duration, label, pip, onSeek }) {
  const pct = duration > 0 ? Math.min(100, Math.max(0, (time / duration) * 100)) : 0;

  const typeStyles = {
    content: {
      stem: { background: 'rgb(var(--color-navy))', width: 3 }, // navy
      pip: { background: 'rgb(var(--color-navy))', width: 20, height: 20, top: -20, fontSize: 9.5 },
    },
    rating: {
      stem: { background: '#A0563B', width: 3 }, // brick
      pip: { background: '#A0563B', width: 20, height: 20, top: -20, fontSize: 9.5 },
    },
    tp: {
      stem: { background: 'rgb(var(--color-coral))', opacity: 0.85 }, // coral
      pip: { background: 'rgb(var(--color-coral))', width: 15, height: 15, top: -15, fontSize: 7.5 },
    },
  };
  const meta = typeStyles[type] || typeStyles.tp;

  return (
    <div
      title={`${label} · ${fmt(time)}`}
      onClick={() => onSeek?.(time)}
      style={{
        position: 'absolute',
        top: 18,
        bottom: 0,
        left: `${pct}%`,
        width: 2,
        transform: 'translateX(-1px)',
        pointerEvents: 'auto',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        const lbl = e.currentTarget.querySelector('[data-lbl]');
        const pipEl = e.currentTarget.querySelector('[data-pip]');
        if (lbl) lbl.style.opacity = 1;
        if (pipEl) pipEl.style.transform = 'translateX(-50%) scale(1.25)';
      }}
      onMouseLeave={(e) => {
        const lbl = e.currentTarget.querySelector('[data-lbl]');
        const pipEl = e.currentTarget.querySelector('[data-pip]');
        if (lbl) lbl.style.opacity = 0;
        if (pipEl) pipEl.style.transform = 'translateX(-50%) scale(1)';
      }}
    >
      {/* Stem */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 2,
          borderRadius: 1,
          boxShadow: '0 0 0 1px #FAF7F2A8',
          ...meta.stem,
        }}
      />
      {/* Pip (label badge) */}
      <div
        data-pip
        className="font-condensed font-bold"
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          borderRadius: '50%',
          border: '2px solid rgb(var(--color-cream))', /* cream halo */
          boxShadow: '0 1px 4px #2C3E5040',
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          letterSpacing: '0.02em',
          transition: 'transform 160ms ease',
          ...meta.pip,
        }}
      >
        {pip}
      </div>
      {/* Hover label */}
      <div
        data-lbl
        className="font-condensed font-bold uppercase"
        style={{
          position: 'absolute',
          top: -42,
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: 9.5,
          letterSpacing: '0.04em',
          background: 'rgb(var(--color-navy))',
          color: '#fff',
          padding: '3px 7px',
          borderRadius: 4,
          whiteSpace: 'nowrap',
          opacity: 0,
          pointerEvents: 'none',
          transition: 'opacity 160ms ease',
          boxShadow: '0 2px 6px #2C3E5030',
        }}
      >
        {label} · {fmt(time)}
      </div>
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────────────── */

export default function WaveformPlayer({
  url,
  uploading,
  onUpload,
  onLoadedMetadata,
  onRemove,
  contentStart,
  ratingStart,
  ratingEnd,
  showRating,
  tappingPoints,
  tappingPointsCatalog,
  className,
}) {
  const waveContainerRef = useRef(null);
  const wsRef = useRef(null);
  const inputRef = useRef(null);
  // Latest-prop ref so the wavesurfer effect can stay keyed on `url`
  // alone without recreating the canvas every time the parent
  // re-renders with a new function identity. We update this on every
  // render and the `onReady` callback reads `.current` lazily.
  const onLoadedMetadataRef = useRef(onLoadedMetadata);
  onLoadedMetadataRef.current = onLoadedMetadata;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);

  /* ─── Instantiate wavesurfer per URL ────────────────────────── */
  useEffect(() => {
    if (!url || !waveContainerRef.current) return undefined;

    // Reset state for a new instance
    setReady(false);
    setLoadError(null);
    setCurrentTime(0);
    setDuration(0);

    let cancelled = false;
    const ws = WaveSurfer.create({
      container: waveContainerRef.current,
      url,
      waveColor: '#DDD3BE',     // lighter sand — bars retreat into bg
      progressColor: 'rgb(var(--color-teal-green))', // teal-green
      cursorColor: '#5C7E72',   // teal-green-d
      barWidth: 2,
      barGap: 1,
      barRadius: 1.5,
      height: 88,
      normalize: true,
      mediaControls: false,
      interact: true,
    });
    wsRef.current = ws;

    const onReady = () => {
      if (cancelled) return;
      setReady(true);
      const d = ws.getDuration();
      setDuration(d);
      // Bubble duration up in a shape compatible with the v1
      // <audio onLoadedMetadata> consumer. Read the handler off the
      // ref so callers can pass a fresh function each render without
      // forcing a wavesurfer rebuild.
      const handler = onLoadedMetadataRef.current;
      if (handler) handler({ target: { duration: d } });
    };
    const onAudioProcess = () => {
      if (cancelled) return;
      setCurrentTime(ws.getCurrentTime());
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onFinish = () => {
      setIsPlaying(false);
      setCurrentTime(ws.getDuration());
    };
    const onErr = (err) => {
      // Wavesurfer surfaces decode errors here; show a quiet fallback
      // rather than crashing the whole form.
      console.warn('wavesurfer:', err);
      setLoadError('Could not decode audio for waveform — playback may not work.');
    };

    ws.on('ready', onReady);
    ws.on('audioprocess', onAudioProcess);
    ws.on('seeking', onAudioProcess);
    ws.on('play', onPlay);
    ws.on('pause', onPause);
    ws.on('finish', onFinish);
    ws.on('error', onErr);

    return () => {
      cancelled = true;
      try {
        ws.destroy();
      } catch {
        /* ignore — destroy may throw if not fully initialized */
      }
      wsRef.current = null;
    };
  }, [url]);

  /* ─── Build the list of markers from props ──────────────────── */
  const markers = useMemo(() => {
    if (!duration || duration <= 0) return [];
    const list = [];

    // Content start — always shown when set
    if (contentStart > 0) {
      list.push({
        key: 'content-start',
        type: 'content',
        time: contentStart,
        pip: 'C',
        label: 'Content start',
      });
    }

    // Rating window — only when the toggle is on
    if (showRating) {
      if (ratingStart > 0) {
        list.push({
          key: 'rating-start',
          type: 'rating',
          time: ratingStart,
          pip: 'R',
          label: 'Rating start',
        });
      }
      if (ratingEnd > 0) {
        list.push({
          key: 'rating-end',
          type: 'rating',
          time: ratingEnd,
          pip: 'R',
          label: 'Rating end',
        });
      }
    }

    // Tapping points — resolve the name via the catalog so the pip
    // initials + hover label read correctly. The catalog is the
    // Redux `tapping_points` array.
    (tappingPoints || []).forEach((tp, i) => {
      if (!tp || !tp.tapping_point_id) return;
      const time = Number(tp.timestamp) || 0;
      if (time <= 0) return;
      const catalogEntry = (tappingPointsCatalog || []).find(
        (c) => Number(c.tapping_point_id) === Number(tp.tapping_point_id),
      );
      const name = catalogEntry?.tapping_point_name || 'Tapping point';
      list.push({
        key: `tp-${i}-${tp.tapping_point_id}`,
        type: 'tp',
        time,
        pip: tpInitials(name),
        label: name,
      });
    });

    return list;
  }, [contentStart, ratingStart, ratingEnd, showRating, tappingPoints, tappingPointsCatalog, duration]);

  /* ─── Handlers ──────────────────────────────────────────────── */
  const handlePlayPause = () => {
    if (wsRef.current) wsRef.current.playPause();
  };
  const handleSeek = (time) => {
    if (wsRef.current && duration > 0) {
      wsRef.current.setTime(time);
      setCurrentTime(time);
    }
  };
  const openPicker = () => {
    if (inputRef.current) inputRef.current.click();
  };
  const handleFile = (e) => {
    if (onUpload) onUpload(e);
    e.target.value = '';
  };

  const filename = deriveFilename(url);

  /* ─── Empty state — render the same upload tile shape so the
       section reads consistently before audio is attached. ──── */
  if (!url) {
    return (
      <div className={cn('flex flex-col', className)} style={{ minWidth: 0 }}>
        <div
          onClick={openPicker}
          style={{
            background: 'rgb(var(--color-cream))',
            border: '1px dashed #D6CCAE',
            borderRadius: 8,
            padding: 14,
            cursor: 'pointer',
            transition: 'all 200ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))';
            e.currentTarget.style.borderStyle = 'solid';
            e.currentTarget.style.background = 'rgb(var(--color-surface))';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = '#D6CCAE';
            e.currentTarget.style.borderStyle = 'dashed';
            e.currentTarget.style.background = 'rgb(var(--color-cream))';
          }}
        >
          <div
            className="flex flex-col items-center"
            style={{ gap: 8, padding: '24px 14px', textAlign: 'center', color: '#9CA1A8' }}
          >
            {UPLOAD_ICON}
            <span style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-navy))' }}>
              Click to upload session audio
            </span>
            <span style={{ fontSize: 11 }}>MP3 / M4A / WAV · live waveform once attached</span>
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="audio/*"
          onChange={handleFile}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        />
      </div>
    );
  }

  /* ─── Populated state — waveform + controls + legend ─────── */
  return (
    <div
      className={cn('ws-wrap', className)}
      style={{
        background: 'rgb(var(--color-cream))', /* cream */
        border: '1px solid rgb(var(--color-sand))', /* sand */
        borderRadius: 8,
        padding: 18,
        minWidth: 0,
        position: 'relative',
      }}
    >
      {/* Stage — waveform canvas + marker overlay. The 24px top
          padding leaves room for the marker pips and hover labels
          that overflow above the canvas. */}
      <div
        style={{
          position: 'relative',
          padding: '24px 6px 6px',
          isolation: 'isolate',
        }}
      >
        <div
          ref={waveContainerRef}
          style={{
            width: '100%',
            cursor: 'pointer',
            position: 'relative',
            zIndex: 1,
            minHeight: 88,
          }}
        />
        {!ready && !loadError && (
          <div
            className="text-text-muted"
            style={{
              position: 'absolute',
              inset: '24px 6px 6px',
              display: 'grid',
              placeItems: 'center',
              background: '#FAF4EAE0',
              fontSize: 12,
              zIndex: 3,
            }}
          >
            Loading waveform…
          </div>
        )}
        {loadError && (
          <div
            className="text-coral-d"
            style={{
              position: 'absolute',
              top: 4,
              right: 6,
              fontSize: 11,
              zIndex: 3,
              background: '#FFF',
              padding: '2px 6px',
              borderRadius: 4,
              border: '1px solid rgb(var(--color-sand))',
            }}
            title={loadError}
          >
            Waveform unavailable
          </div>
        )}

        {/* Marker layer — sits above the wave, but the wave's
            click-to-seek still works because each marker's stem is
            only 2-3px wide. */}
        <div
          style={{
            position: 'absolute',
            left: 6,
            right: 6,
            top: 0,
            bottom: 6,
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          {markers.map((m) => (
            <Marker
              key={m.key}
              type={m.type}
              time={m.time}
              duration={duration}
              label={m.label}
              pip={m.pip}
              onSeek={handleSeek}
            />
          ))}
        </div>
      </div>

      {/* Controls row */}
      <div
        className="flex items-center"
        style={{
          gap: 14,
          marginTop: 14,
          paddingTop: 14,
          borderTop: '1px solid #ECE3D2', /* line */
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          onClick={handlePlayPause}
          disabled={!ready}
          aria-label={isPlaying ? 'Pause' : 'Play'}
          className="grid place-items-center cursor-pointer"
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'rgb(var(--color-teal-green))', /* teal-green */
            color: '#fff',
            border: 0,
            opacity: ready ? 1 : 0.5,
            boxShadow: '0 2px 6px #75A29360, inset 0 1px 0 #FFFFFF30',
            flexShrink: 0,
          }}
        >
          {isPlaying ? PAUSE_ICON : PLAY_ICON}
        </button>
        <span
          className="font-mono text-navy"
          style={{ fontSize: 13, fontWeight: 600, minWidth: 110 }}
        >
          {fmt(currentTime)} / {fmt(duration)}
        </span>
        <span
          className="text-text-muted"
          style={{
            fontSize: 11.5,
            marginLeft: 'auto',
            marginRight: 8,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 320,
          }}
          title={filename}
        >
          {filename}
          <span className="font-mono" style={{ marginLeft: 6, color: '#9CA1A8' }}>
            · session_main_url
          </span>
        </span>
        <button
          type="button"
          onClick={openPicker}
          className="font-condensed font-bold uppercase cursor-pointer"
          style={{
            height: 28,
            padding: '0 10px',
            border: '1px solid rgb(var(--color-sand))',
            borderRadius: 4,
            background: 'rgb(var(--color-surface))',
            color: 'rgb(var(--color-navy))',
            fontSize: 11,
            letterSpacing: '0.06em',
          }}
        >
          Replace
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="font-condensed font-bold uppercase cursor-pointer"
            style={{
              height: 28,
              padding: '0 10px',
              border: '1px solid transparent',
              borderRadius: 4,
              background: 'transparent',
              color: '#C4715B', /* coral-d */
              fontSize: 11,
              letterSpacing: '0.06em',
            }}
          >
            Remove
          </button>
        )}
        {uploading && (
          <span className="text-teal-d" style={{ fontSize: 11.5, fontWeight: 600 }}>
            Uploading…
          </span>
        )}
      </div>

      {/* Legend */}
      <div
        className="flex flex-wrap text-text-muted"
        style={{ marginTop: 12, gap: 14, fontSize: 11 }}
      >
        <span className="inline-flex items-center" style={{ gap: 6 }}>
          <i style={{ width: 9, height: 9, borderRadius: '50%', background: 'rgb(var(--color-navy))', display: 'inline-block' }} />
          Content start
        </span>
        {showRating && (
          <span className="inline-flex items-center" style={{ gap: 6 }}>
            <i style={{ width: 9, height: 9, borderRadius: '50%', background: '#A0563B', display: 'inline-block' }} />
            Rating prompt (start &amp; end)
          </span>
        )}
        <span className="inline-flex items-center" style={{ gap: 6 }}>
          <i style={{ width: 9, height: 9, borderRadius: '50%', background: 'rgb(var(--color-coral))', display: 'inline-block' }} />
          Tapping point
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        onChange={handleFile}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
    </div>
  );
}
